import { supabase } from '../supabaseClient';
import { normalizeProgramCellBgValue } from '../utils/programCellBgStyle';

const TABLE = 'user_program_cell_bg_palettes';
const CACHE_PREFIX = 'cp.programCellBgPalette.v1.';
export const PROGRAM_CELL_BG_PALETTE_MAX = 30;

export interface ProgramCellBgPaletteSnapshot {
  colors: string[];
  /** 서버 updated_at (테이블 미적용·미저장이면 null) */
  updatedAt: string | null;
}

export function normalizeProgramCellBgPalette(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    const v = normalizeProgramCellBgValue(item);
    if (v && !out.includes(v)) out.push(v);
    if (out.length >= PROGRAM_CELL_BG_PALETTE_MAX) break;
  }
  return out;
}

function isMissingTableError(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  if (error.code === '42P01' || error.code === 'PGRST205') return true;
  return /relation .* does not exist|Could not find the table/i.test(String(error.message ?? ''));
}

let tableAvailable: boolean | null = null;

function cacheKey(userId: string): string {
  return `${CACHE_PREFIX}${userId}`;
}

export function readProgramCellBgPaletteCache(userId: string): ProgramCellBgPaletteSnapshot | null {
  if (!userId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { colors?: unknown; updatedAt?: unknown };
    return {
      colors: normalizeProgramCellBgPalette(parsed.colors),
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : null,
    };
  } catch {
    return null;
  }
}

function writeCache(userId: string, snap: ProgramCellBgPaletteSnapshot): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(cacheKey(userId), JSON.stringify(snap));
  } catch {
    // ignore quota
  }
}

export function clearProgramCellBgPaletteCaches(): void {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

/** 캐시가 있으면 updated_at head만 비교하고, 다를 때만 colors를 다시 읽는다. */
export async function fetchProgramCellBgPalette(
  userId: string,
  options?: { force?: boolean }
): Promise<ProgramCellBgPaletteSnapshot> {
  const cached = readProgramCellBgPaletteCache(userId);
  if (!userId || tableAvailable === false) return cached ?? { colors: [], updatedAt: null };

  if (cached && !options?.force) {
    const head = await supabase.from(TABLE).select('updated_at').eq('user_id', userId).maybeSingle();
    if (head.error) {
      if (isMissingTableError(head.error)) {
        tableAvailable = false;
        return cached;
      }
      throw new Error(head.error.message);
    }
    tableAvailable = true;
    const remoteUpdatedAt = (head.data?.updated_at as string | undefined) ?? null;
    if (remoteUpdatedAt === cached.updatedAt) return cached;
    if (!remoteUpdatedAt) {
      // 서버 행이 없고 로컬에만 있는 색(테이블 적용 전 등록분)은 유지
      return cached;
    }
  }

  const { data, error } = await supabase.from(TABLE).select('colors, updated_at').eq('user_id', userId).maybeSingle();
  if (error) {
    if (isMissingTableError(error)) {
      tableAvailable = false;
      return cached ?? { colors: [], updatedAt: null };
    }
    throw new Error(error.message);
  }
  tableAvailable = true;
  const snap: ProgramCellBgPaletteSnapshot = data
    ? { colors: normalizeProgramCellBgPalette(data.colors), updatedAt: (data.updated_at as string) ?? null }
    : { colors: cached?.colors ?? [], updatedAt: null };
  writeCache(userId, snap);
  return snap;
}

/**
 * 최신 서버 값에 변경 함수를 적용해 저장한다(다른 탭·기기 변경을 덮어쓰지 않도록 read-modify-write).
 * 테이블이 아직 없으면 로컬 캐시에만 반영한다.
 */
export async function mutateProgramCellBgPalette(
  userId: string,
  mutate: (colors: string[]) => string[]
): Promise<ProgramCellBgPaletteSnapshot> {
  const base = await fetchProgramCellBgPalette(userId, { force: true });
  const nextColors = normalizeProgramCellBgPalette(mutate([...base.colors]));
  if (tableAvailable === false) {
    const snap = { colors: nextColors, updatedAt: null };
    writeCache(userId, snap);
    return snap;
  }
  const updatedAt = new Date().toISOString();
  const { data, error } = await supabase
    .from(TABLE)
    .upsert({ user_id: userId, colors: nextColors, updated_at: updatedAt }, { onConflict: 'user_id' })
    .select('colors, updated_at')
    .maybeSingle();
  if (error) {
    if (isMissingTableError(error)) {
      tableAvailable = false;
      const snap = { colors: nextColors, updatedAt: null };
      writeCache(userId, snap);
      return snap;
    }
    throw new Error(error.message);
  }
  const snap: ProgramCellBgPaletteSnapshot = {
    colors: data ? normalizeProgramCellBgPalette(data.colors) : nextColors,
    updatedAt: (data?.updated_at as string | undefined) ?? updatedAt,
  };
  writeCache(userId, snap);
  return snap;
}
